import React, { useCallback, useState } from 'react';
import { networkPostUploadFile } from 'src/network/FileManager';
import useImageInfoStore from 'src/store/uploader';
import { Button, InputAdornment, Tooltip } from '@mui/material';
import { Controller } from 'react-hook-form';
import Iconify from '../Iconify';
import { useSnackbar } from 'notistack';
import { DialogAnimate } from '../animate';
import { useDropzone } from 'react-dropzone';
import ImageWithRetry from './ImageWithRetry';
import { RHFTextField } from '../hook-form';

const GeneralUpload = ({ name, label, id, afterChangeAction, maxFileSizeMB, isImageOnly = true, ...rest }) => {
  const { enqueueSnackbar } = useSnackbar();

  const { setImageInfo } = useImageInfoStore();
  const maxFileSize = maxFileSizeMB ? maxFileSizeMB * 1024 * 1024 : null;

  const [loading, setLoading] = useState(false);

  const [image, setImage] = useState();
  const [isOpenModalImage, setIsOpenModalImage] = useState(false);

  const acceptValue = isImageOnly ? 'image/*' : undefined;

  // -------------------------------------------------------------------

  const uploadFileHandler = (form_data) => {
    setLoading(true);
    networkPostUploadFile(form_data)
      .then((res) => {
        if (res.data.Status === 'OK') {
          afterChangeAction(res.data.UrlFile);
          setImage(res.data.UrlFile);
        }
      })
      .catch((error) => {
        enqueueSnackbar('مشکلی پیش آمده، لطفا مجددا امتحان کنید', { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChangeFile = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      if (maxFileSize && file.size > maxFileSize) {
        enqueueSnackbar(`فایل انتخابی بیش از ${maxFileSizeMB} مگابایت است`, { variant: 'error' });
        return;
      }
      let form_data = new FormData();
      form_data.append('file', file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();

        reader.onload = (e) => {
          const img = new Image();
          img.src = e.target.result;

          img.onload = () => {
            const width = img.width;
            const height = img.height;

            setImageInfo({
              width,
              height,
              name: file?.name,
              size: file?.size,
              type: file?.type,
            });

            form_data.append('width', width);
            form_data.append('height', height);

            uploadFileHandler(form_data);
          };
        };

        reader.readAsDataURL(file);
      } else {
        setImageInfo({
          name: file.name,
          size: file.size,
          type: file.type,
        });

        uploadFileHandler(form_data);
      }
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    handleChangeFile(acceptedFiles);
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: acceptValue,
    noClick: true,
    noKeyboard: true,
    ...(maxFileSize && {
      maxSize: maxFileSize,
      onDropRejected: () => enqueueSnackbar(`حجم فایل نباید بیشتر از ${maxFileSizeMB} مگابایت باشد`, { variant: 'error' }),
    }),
  });

  return (
    <>
      <Controller
        name={name}
        render={({ field }) => (
          <RHFTextField
            dir="ltr"
            helperText={maxFileSizeMB ? `حداکثر حجم مجاز فایل: ${maxFileSizeMB} مگابایت` : rest.helperText}
            fullWidth
            variant="outlined"
            label={label}
            value={field.value || ''}
            autoComplete="off"
            {...getRootProps()}
            {...rest}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <input
                    id={id}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => handleChangeFile(e.target.files)}
                    {...getInputProps()}
                  />
                  {isImageOnly && (field.value || image) ? (
                    <Button
                      size="small"
                      color="inherit"
                      variant="contained"
                      sx={{ ml: 1 }}
                      onClick={(event) => {
                        event.stopPropagation();
                        setImage(field.value);
                        setIsOpenModalImage(true);
                      }}
                    >
                      مشاهده
                    </Button>
                  ) : null}
                  <span className="cursor-pointer absolute left-2" onClick={open}>
                    {loading ? (
                      <Iconify width={30} height={30} icon={'svg-spinners:270-ring'} />
                    ) : (
                      <Tooltip title="آپلود تصویر">
                        <span>
                          {/* <Iconify width={30} height={30} icon={'ep:upload-filled'} /> */}
                          <svg
                            aria-hidden="true"
                            role="img"
                            class="MuiBox-root rtl-c2uxn6 iconify iconify--ep"
                            width="1em"
                            height="1em"
                            preserveAspectRatio="xMidYMid meet"
                            viewBox="0 0 1024 1024"
                          >
                            <path
                              fill="currentColor"
                              d="M544 864V672h128L512 480L352 672h128v192H320v-1.6c-5.4.3-10.5 1.6-16 1.6A240 240 0 0 1 64 624a239 239 0 0 1 212.6-237.2A240 240 0 0 1 512 192a240 240 0 0 1 235.5 194.8A239 239 0 0 1 959.9 624a240 240 0 0 1-240 240c-5.3 0-10.5-1.3-16-1.6v1.6z"
                            ></path>
                          </svg>
                        </span>
                      </Tooltip>
                    )}
                  </span>
                </InputAdornment>
              ),
              sx: { paddingRight: '45px' },
            }}
            {...field}
          />
        )}
      />

      <DialogAnimate open={isOpenModalImage} onClose={() => setIsOpenModalImage(false)} sx={{ backgroundColor: 'transparent' }}>
        <ImageWithRetry src={image} className="w-full h-full object-cover" />
      </DialogAnimate>
    </>
  );
};

export default GeneralUpload;
